import type { AuthError } from '@supabase/supabase-js';

export const DUPLICATE_EMAIL_MESSAGE = 'An account already exists with this email. Please sign in instead.';

export const EMAIL_RATE_LIMIT_MESSAGE =
  'Too many confirmation emails were sent. Please wait a few minutes and try again.';

/** User-friendly copy for Supabase resend / auth rate limits. */
export function mapAuthRateLimitMessage(message: string): string {
  if (/rate limit/i.test(message)) return EMAIL_RATE_LIMIT_MESSAGE;
  const secondsMatch = message.match(/after (\d+) seconds?/i);
  if (secondsMatch) {
    return `Please wait ${secondsMatch[1]} seconds before requesting another email.`;
  }
  return message;
}

export class DuplicateEmailError extends Error {
  constructor() {
    super(DUPLICATE_EMAIL_MESSAGE);
    this.name = 'DuplicateEmailError';
  }
}

export function isDuplicateEmailError(err: unknown): boolean {
  if (err instanceof DuplicateEmailError) return true;
  return err instanceof Error && err.message === DUPLICATE_EMAIL_MESSAGE;
}

/** Map Supabase signUp errors to user-friendly copy. */
export function mapSignupError(error: AuthError | Error): string {
  const authErr = error as AuthError;
  const code = authErr.code;
  const message = error.message || '';

  if (code === 'user_already_exists') return DUPLICATE_EMAIL_MESSAGE;
  if (/already registered/i.test(message)) return DUPLICATE_EMAIL_MESSAGE;
  if (/already been registered/i.test(message)) return DUPLICATE_EMAIL_MESSAGE;
  if (/email address is already/i.test(message)) return DUPLICATE_EMAIL_MESSAGE;
  if (/user already exists/i.test(message)) return DUPLICATE_EMAIL_MESSAGE;
  if (/already exists/i.test(message)) return DUPLICATE_EMAIL_MESSAGE;
  if (code === 'over_email_send_rate_limit') return EMAIL_RATE_LIMIT_MESSAGE;
  if (/email rate limit/i.test(message)) return EMAIL_RATE_LIMIT_MESSAGE;
  if (/rate limit exceeded/i.test(message)) return EMAIL_RATE_LIMIT_MESSAGE;

  return message || 'Failed to create account. Please try again.';
}

export function isEmailRateLimitError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return err.message === EMAIL_RATE_LIMIT_MESSAGE;
}

type SignUpResponse = {
  user: {
    created_at?: string;
    identities?: { id: string }[] | null;
  } | null;
  session: unknown;
};

/**
 * Supabase may return HTTP 200 for duplicate emails (anti-enumeration) or sign in
 * an existing user when email confirmation is off. Reject those before showing success.
 */
export function assertNewSignUpUser(data: SignUpResponse): void {
  const user = data.user;
  if (!user) return;

  if (Array.isArray(user.identities) && user.identities.length === 0) {
    throw new DuplicateEmailError();
  }

  if (data.session && user.created_at) {
    const createdMs = new Date(user.created_at).getTime();
    if (Number.isFinite(createdMs) && Date.now() - createdMs > 120_000) {
      throw new DuplicateEmailError();
    }
  }
}
